import { useEffect, useRef, useState } from "react";
import {
  ensureThread, sendMessage, subscribeMessages, CHAT_MSG_MAX,
} from "../services/chat";
import { readAppUser } from "../services/auth";

// Sliding drawer that hosts a private in-website chat thread. Opens
// from the "💬 CHAT" button on a forum order — the clicker is `me`,
// the order poster is `other`. Thread is looked up (or created) via
// `ensureThread` on mount; messages stream live via onSnapshot.

export default function ChatDrawer({ me, other, orderContext, onClose }) {
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const scrollerRef = useRef(null);

  // Resolve (or create) the thread for me ↔ other.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer usernames from the app_users docs (unchanged from role-pick).
        const [meDoc, otherDoc] = await Promise.all([
          readAppUser(me.uid),
          readAppUser(other.uid),
        ]);
        if (cancelled) return;
        const id = await ensureThread({
          myUid: me.uid,
          otherUid: other.uid,
          myUsername: meDoc?.username || me.displayName || null,
          otherUsername: otherDoc?.username || other.username || null,
        });
        if (!cancelled) setThreadId(id);
      } catch (e) {
        if (!cancelled) setErr(e.message || "Couldn't open the chat — try again.");
      }
    })();
    return () => { cancelled = true; };
  }, [me.uid, other.uid]);

  // Subscribe to the message stream once the thread ID is known.
  useEffect(() => {
    if (!threadId) return;
    return subscribeMessages(threadId, setMessages);
  }, [threadId]);

  // Auto-scroll to the newest message whenever the list grows.
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const canSend = threadId && text.trim().length > 0 && !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    setErr(null);
    try {
      await sendMessage(threadId, me.uid, text);
      setText("");
    } catch (er) {
      setErr(er.message || String(er));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="cd-backdrop" onClick={onClose} role="presentation">
      <div
        className="cd-drawer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Chat with @${other.username || "user"}`}
      >
        <style>{drawerCss}</style>

        <header className="cd-header">
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 11, letterSpacing: 2, color: "#7a7d86", fontWeight: 800 }}>
              CHAT WITH
            </div>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#eaeaef", wordBreak: "break-word" }}>
              @{other.username || "user"}
            </div>
          </div>
          <button className="cd-close" onClick={onClose} aria-label="Close chat">✕</button>
        </header>

        {orderContext && (
          <div className="cd-context">
            <span style={{ fontSize: 10, letterSpacing: 1.6, color: "#7a7d86", fontWeight: 900 }}>
              RE: ORDER
            </span>
            <div style={{ marginTop: 3, color: "#c8ced5", fontSize: 12.5 }}>
              {orderContext.game && <b>{orderContext.game}</b>}
              {orderContext.quantity && (
                <> · {fmtQtyShort(orderContext.quantity)} {String(orderContext.segment || "").toUpperCase()}</>
              )}
              {typeof orderContext.maxRate === "number" && (
                <> · ≤ ₹{orderContext.maxRate.toFixed(orderContext.maxRate >= 100 ? 0 : 2)}/1k</>
              )}
            </div>
          </div>
        )}

        <div className="cd-messages" ref={scrollerRef}>
          {!threadId && <div className="cd-empty">Opening chat…</div>}
          {threadId && messages.length === 0 && (
            <div className="cd-empty">
              No messages yet. Send the first one.
            </div>
          )}
          {messages.map((m) => {
            const mine = m.from === me.uid;
            return (
              <div key={m.id} className={`cd-msg ${mine ? "mine" : "theirs"}`}>
                <div className="cd-bubble">{m.text}</div>
                <div className="cd-time">{fmtTime(m.createdAt)}</div>
              </div>
            );
          })}
        </div>

        {err && <div className="cd-err">⚠️ {err}</div>}

        <form className="cd-compose" onSubmit={submit}>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            rows={2}
            maxLength={CHAT_MSG_MAX}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                if (canSend) submit(e);
              }
            }}
          />
          <button type="submit" disabled={!canSend}>
            {sending ? "…" : "Send"}
          </button>
        </form>
      </div>
    </div>
  );
}

function fmtQtyShort(n) {
  if (typeof n !== "number") return "";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

function fmtTime(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
      ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : d.toLocaleDateString([], { month: "short", day: "numeric" }) +
        " · " + d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const drawerCss = `
  .cd-backdrop {
    position: fixed; inset: 0;
    background: rgba(0,0,0,0.55);
    z-index: 2000;
    display: flex; justify-content: flex-end;
  }
  .cd-drawer {
    width: 100%; max-width: 460px; height: 100%;
    background: #14161c;
    display: flex; flex-direction: column;
    box-shadow: -8px 0 30px rgba(0,0,0,0.6);
    animation: cd-slide 200ms ease-out;
  }
  @keyframes cd-slide {
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
  }
  .cd-header {
    display: flex; align-items: center;
    gap: 10px; padding: 14px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .cd-header > div:first-child { flex: 1; }
  .cd-close {
    background: transparent; border: none;
    color: #9ca0a8; cursor: pointer;
    font-size: 20px; padding: 4px 8px;
    line-height: 1;
  }
  .cd-close:hover { color: #eaeaef; }
  .cd-context {
    padding: 10px 16px;
    background: rgba(76,110,245,0.08);
    border-bottom: 1px solid rgba(255,255,255,0.06);
  }
  .cd-messages {
    flex: 1; overflow-y: auto;
    padding: 14px 16px;
    display: flex; flex-direction: column; gap: 10px;
  }
  .cd-empty {
    color: #7a7d86; text-align: center;
    font-size: 13px; font-style: italic;
    padding: 24px 0;
  }
  .cd-msg { max-width: 82%; display: flex; flex-direction: column; }
  .cd-msg.mine   { align-self: flex-end;   align-items: flex-end; }
  .cd-msg.theirs { align-self: flex-start; align-items: flex-start; }
  .cd-bubble {
    padding: 8px 12px; border-radius: 12px;
    font-size: 13.5px; line-height: 1.35;
    white-space: pre-wrap; word-break: break-word;
  }
  .cd-msg.mine .cd-bubble {
    background: #4c6ef5; color: #fff;
    border-bottom-right-radius: 3px;
  }
  .cd-msg.theirs .cd-bubble {
    background: rgba(255,255,255,0.06); color: #eaeaef;
    border-bottom-left-radius: 3px;
  }
  .cd-time { font-size: 10px; color: #7a7d86; margin-top: 2px; padding: 0 2px; }
  .cd-err {
    padding: 8px 16px; color: #ff6f61;
    font-size: 12.5px;
    background: rgba(255,111,97,0.06);
  }
  .cd-compose {
    display: flex; gap: 8px; padding: 10px 12px;
    border-top: 1px solid rgba(255,255,255,0.06);
    background: rgba(0,0,0,0.25);
  }
  .cd-compose textarea {
    flex: 1; resize: none;
    padding: 8px 10px; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.12);
    background: rgba(0,0,0,0.35); color: #eaeaef;
    font-size: 13.5px; font-family: inherit;
    min-width: 0;
  }
  .cd-compose button {
    padding: 8px 18px; border-radius: 8px;
    border: none; background: #4c6ef5; color: #fff;
    font-weight: 800; font-size: 12.5px;
    letter-spacing: 0.8px; cursor: pointer;
  }
  .cd-compose button:disabled {
    background: rgba(255,255,255,0.06); color: #5c6069;
    cursor: not-allowed;
  }
`;
